import { AlertTriangle, Trash2, Check } from 'lucide-react';
import { useTranslation } from '../../context/LanguageContext';
import Button from './Button';

/**
 * ConfirmModal - Reusable confirmation modal component
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is visible
 * @param {Function} props.onClose - Called when modal is closed/cancelled
 * @param {Function} props.onConfirm - Called when user confirms action
 * @param {string} props.title - Modal title
 * @param {string} props.message - Main message/question
 * @param {React.ReactNode} props.children - Optional content to display (e.g., item details)
 * @param {string} props.confirmText - Text for confirm button (default: "Delete")
 * @param {string} props.cancelText - Text for cancel button (default: "Cancel")
 * @param {React.ReactNode} props.icon - Icon to display in header (default: AlertTriangle)
 * @param {React.ReactNode} props.confirmIcon - Icon for confirm button (default: Trash2, set to null to hide)
 * @param {'danger'|'warning'|'theme'} props.variant - Color variant (default: 'danger')
 *   - danger: red gradient (for deletions)
 *   - warning: amber/yellow gradient
 *   - theme: cabaza purple/pink gradient (for general confirmations)
 * @param {boolean} props.loading - Show loading state on confirm button
 */
const ConfirmModal = ({
    isOpen,
    onClose,
    onConfirm,
    title,
    message,
    children,
    confirmText,
    cancelText,
    icon: Icon = AlertTriangle,
    confirmIcon: ConfirmIcon = Trash2,
    variant = 'danger',
    loading = false
}) => {
    const { t } = useTranslation();

    if (!isOpen) return null;

    // Variant styles
    const variants = {
        danger: {
            header: 'bg-gradient-to-r from-red-500 to-red-600',
            buttonVariant: 'danger',
            iconBg: 'bg-white/20'
        },
        warning: {
            header: 'bg-gradient-to-r from-amber-500 to-orange-500',
            buttonVariant: 'danger',
            iconBg: 'bg-white/20'
        },
        theme: {
            header: 'bg-gradient-to-r from-cabaza-purple to-cabaza-pink',
            buttonVariant: 'primary',
            iconBg: 'bg-white/20'
        }
    };

    const styles = variants[variant] || variants.danger;

    // Default icons based on variant
    const defaultConfirmIcon = variant === 'theme' ? Check : Trash2;
    const finalConfirmIcon = ConfirmIcon === undefined ? defaultConfirmIcon : ConfirmIcon;

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
                {/* Header */}
                <div className={`${styles.header} p-6 text-white`}>
                    <div className="flex items-center gap-3">
                        <div className={`p-2 ${styles.iconBg} rounded-full`}>
                            <Icon className="w-6 h-6" />
                        </div>
                        <h2 className="text-xl font-bold">{title}</h2>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6">
                    {message && (
                        <p className="text-gray-800 dark:text-gray-200 font-medium mb-4">{message}</p>
                    )}
                    {children}
                </div>

                {/* Footer */}
                <div className="border-t border-gray-200 dark:border-gray-700 p-4 flex justify-end gap-3 bg-gray-50 dark:bg-gray-900">
                    <Button
                        variant="ghost"
                        onClick={onClose}
                    >
                        {cancelText || t.common?.cancel || 'Annuler'}
                    </Button>
                    <Button
                        variant={styles.buttonVariant}
                        onClick={onConfirm}
                        icon={finalConfirmIcon}
                        loading={loading}
                    >
                        {confirmText || t.common?.delete || 'Supprimer'}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default ConfirmModal;
